import { faAngleDown, faAngleRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { type HeadFC, Link } from 'gatsby'
import { useEffect, useRef, useState } from 'react'
import type { PageContextPropsType } from '../../../../i18n/i18n'
import { Layout } from '../../../components/foundation/v2/Layout'
import { Seo } from '../../../components/foundation/v2/Seo'
import ContactBanner from '../../../components/v1/ContactBanner'
import SubHeader from '../../../components/v1/product/SubHeader'
import OGImage from '../../../images/ogp/og_talk.png'
import * as SiteWideStyles from '../../../styles/abstract/SiteWide.css'
import * as FeatureTalkPageStyles from '../../../styles/pages/feature/talk/Page.css'

const contentful = require('contentful')

const FeatureTalkPage = ({ pageContext }: PageContextPropsType) => {
  const featureInnerRef = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [interviews, setInterviews] = useState([])
  const [logos, setLogos] = useState([])

  const toggleOpen = () => setOpen(!open)

  const heightAnimation = open ? featureInnerRef.current?.scrollHeight : '380px'

  useEffect(() => {
    const client = contentful.createClient({
      space: '9i61sv81chr5',
      accessToken: 'cd8f709c87ba6f6c5603e1656654a6d921e7a15f039ea48ec3d0d3270d0c8060',
    })

    const pageID = '1yvlqeoKPaPbSKmyJwxttg'

    // お客様事例の取得
    client
      .getEntry(pageID)
      .then((entry: any) => {
        const interviewsEntry = entry.fields.interview
        setInterviews(
          interviewsEntry.map(({ fields }: any) => ({
            title: fields.title,
            clipurl: fields.clipurl,
            imageUrl: fields.image.fields.file.url,
            company: fields.company,
            product: fields.product,
          }))
        )
      })
      .catch((err: any) => console.log(err))

    // 導入実績のロゴ取得
    client
      .getEntry(pageID)
      .then((entry: any) => {
        const logosEntry = entry.fields.companies
        setLogos(
          logosEntry.map(({ fields }: any) => ({
            imageUrl: fields.logo.fields.file.url,
            companyName: fields.companyName,
          }))
        )
      })
      .catch((err: any) => console.log(err))
  }, [])

  const features = [
    {
      title: 'サポートテンプレート',
      text: 'ウェブチャット・FAQなどのサポート施策を専用テンプレートを用いて配信できます。',
      icon: 'icon_feature_1',
    },
    {
      title: 'アクションカスタマイズ',
      text: (
        <>
          施策を自由にカスタマイズできます。
          <br />
          ※HTML/CSS/JavaScriptの知識が必要となります
        </>
      ),
      icon: 'icon_feature_2',
    },
    {
      title: 'ABテスト',
      text: '複数アクションのパターンテストを行って、効果検証を行うことができます。',
      icon: 'icon_feature_3',
    },
    {
      title: '自動応答(β)',
      text: 'ユーザーの質問内容ごとに自動で回答する仕組みを構築できます。',
      icon: 'icon_feature_4',
    },
    {
      title: 'チャットアクションレポート',
      text: 'チャットやサポートテンプレートの効果をレポートで確認できます。',
      icon: 'icon_feature_1',
    },
    {
      title: 'チャットユーザーのデータ可視化',
      text: '問合せユーザーのセグメントやユーザー情報をトーク画面で確認できます。',
      icon: 'icon_feature_6',
    },
    {
      title: 'チャットオペレーションレポート',
      text: 'オペレーター全体/個別のパフォーマンスをレポートで確認できます。',
      icon: 'icon_feature_7',
    },
    {
      title: 'チームコラボレーション',
      text: 'チーム機能/オペレーター・SV間での情報共有などチームでのサポート運用をスムーズに行えます。',
      icon: 'icon_feature_8',
    },
    {
      title: '自動アサイン',
      text: 'ユーザーのセグメントやページに合ったオペレーターの自動アサインができます。',
      icon: 'icon_feature_9',
    },
    {
      title: '生産性向上',
      text: '定型文登録やユーザーからの質問時の通知設定で、効率的にオペレーションを回すことができます。',
      icon: 'icon_feature_10',
    },
  ]
  return (
    <Layout pageContext={pageContext}>
      <main className={FeatureTalkPageStyles.Container} data-classname="FeatureTalkPageStyles-Container">
        <section className={FeatureTalkPageStyles.Hero} data-classname="FeatureTalkPageStyles-Hero">
          <div className={FeatureTalkPageStyles.Hero_Inner} data-classname="FeatureTalkPageStyles-Hero_Inner">
            <div className={FeatureTalkPageStyles.Hero_Cover} data-classname="FeatureTalkPageStyles-Hero_Cover">
              <img
                className={FeatureTalkPageStyles.Hero_Image}
                src="/assets/images/talk/main_visual.png"
                alt="KARTEトーク画面"
                data-classname="FeatureTalkPageStyles-Hero_Image"
              />
            </div>
            <div className={FeatureTalkPageStyles.Hero_Content} data-classname="FeatureTalkPageStyles-Hero_Content">
              <h1 className={FeatureTalkPageStyles.Hero_Headline} data-classname="FeatureTalkPageStyles-Hero_Headline">
                KARTE Talk
              </h1>
              <h2
                className={FeatureTalkPageStyles.Hero_SubHeadline}
                data-classname="FeatureTalkPageStyles-Hero_SubHeadline"
              >
                柔軟に設計できる
                <br />
                サポートアクション
              </h2>
              <Link
                className={FeatureTalkPageStyles.Hero_Button}
                to="/enterprise/"
                target="_blank"
                data-category="body"
                data-action="cv"
                data-label="お問い合わせ"
                data-classname="FeatureTalkPageStyles-Hero_Button"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>
        <SubHeader />
        <section className={FeatureTalkPageStyles.Benefit} id="benefit">
          <div className={FeatureTalkPageStyles.Benefit_Inner} data-classname="FeatureTalkPageStyles-Benefit_Inner">
            <div
              className={FeatureTalkPageStyles.Benefit_Primary}
              data-classname="FeatureTalkPageStyles-Benefit_Primary"
            >
              <h2
                className={FeatureTalkPageStyles.Benefit_Headline}
                data-classname="FeatureTalkPageStyles-Benefit_Headline"
              >
                <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                  想像した通り
                </b>
                にサポート体験を作り込める
              </h2>
              <p>
                顧客満足度の高いサポート体験には、チャットデザインや言い回し、誘導先など、
                <br className={SiteWideStyles.DisplayDesktopOnly} />
                テストを繰り返しながら精度を高めていくことが不可欠です。
                <br />
                KARTE
                Talkでは柔軟に編集できるチャット/FAQテンプレートを用いて、イメージ通りのサポート施策を提供することができます。
              </p>
              <div className={FeatureTalkPageStyles.Benefit_Image} data-classname="FeatureTalkPageStyles-Benefit_Image">
                <img
                  className={FeatureTalkPageStyles.Benefit_Illustration}
                  src="/assets/images/talk/illustration_benefit_1.png"
                  alt="想像した通りにサポート体験を作り込める"
                  data-classname="FeatureTalkPageStyles-Benefit_Illustration"
                />
              </div>
            </div>
            <ul>
              <li className={FeatureTalkPageStyles.Benefit_Item} data-classname="FeatureTalkPageStyles-Benefit_Item">
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Image}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Image"
                >
                  <img
                    className={FeatureTalkPageStyles.Benefit_Item_Illustration}
                    src="/assets/images/talk/illustration_benefit_2.png"
                    alt="スムーズな課題解決を促して、サポートコストを低減"
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Illustration"
                  />
                </div>
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Content}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Content"
                >
                  <dl
                    className={FeatureTalkPageStyles.Benefit_Item_Catalog}
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog"
                  >
                    <dt
                      className={FeatureTalkPageStyles.Benefit_Item_Catalog_Heading}
                      data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Heading"
                    >
                      スムーズな課題解決を促して、
                      <br />
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        サポートコストを低減
                      </b>
                    </dt>
                    <dd>
                      全てのサポートシーンで有人対応は本当に必要でしょうか？
                      <br />
                      例えば、よくある質問へのFAQを最初に顧客にご案内することで多くの自己解決が生まれる可能性があります。必要な場合は有人チャットに繋ぐことも可能です。柔軟にサポートの入り口を設けることでノンボイス比率が向上し、結果サポートコストを下げることができます。
                    </dd>
                  </dl>
                </div>
              </li>
              <li className={FeatureTalkPageStyles.Benefit_Item} data-classname="FeatureTalkPageStyles-Benefit_Item">
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Image}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Image"
                >
                  <img
                    className={FeatureTalkPageStyles.Benefit_Item_Illustration}
                    src="/assets/images/talk/illustration_benefit_3.png"
                    alt="体制に最適なオペレーションを構築する"
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Illustration"
                  />
                </div>
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Content}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Content"
                >
                  <dl
                    className={FeatureTalkPageStyles.Benefit_Item_Catalog}
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog"
                  >
                    <dt
                      className={FeatureTalkPageStyles.Benefit_Item_Catalog_Heading}
                      data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Heading"
                    >
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        体制に最適な
                      </b>
                      <br />
                      オペレーションを構築する
                    </dt>
                    <dd>
                      チームの規模を問わず、目的に応じた顧客サポートができる機能を提供します。複数のサポートチームや自動アサイン、権限管理、誰でも扱える定型文を組み合わせることで体制に合ったサポートを構築でき、堅牢なセキュリティを基盤に安全に運用することができます。
                    </dd>
                  </dl>
                </div>
              </li>
              <li className={FeatureTalkPageStyles.Benefit_Item} data-classname="FeatureTalkPageStyles-Benefit_Item">
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Image}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Image"
                >
                  <img
                    className={FeatureTalkPageStyles.Benefit_Item_Illustration}
                    src="/assets/images/talk/illustration_benefit_4.png"
                    alt="レポートで正しく状況を知り、先につなげる"
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Illustration"
                  />
                </div>
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Content}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Content"
                >
                  <dl
                    className={FeatureTalkPageStyles.Benefit_Item_Catalog}
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog"
                  >
                    <dt
                      className={FeatureTalkPageStyles.Benefit_Item_Catalog_Heading}
                      data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Heading"
                    >
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        レポート
                      </b>
                      で正しく状況を知り、
                      <br className={SiteWideStyles.DisplayDesktopOnly} />
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        先につなげる
                      </b>
                    </dt>
                    <dd>
                      オペレーションが問題なく回っているか、適切なサポート案内ができているか。サポート体験を評価するためには、サポートオペレーションと施策効果の両面を正確に把握する必要があります。課題点をレポートで見つけて、素早くPDCAを回しましょう。
                    </dd>
                  </dl>
                </div>
              </li>
              <li className={FeatureTalkPageStyles.Benefit_Item} data-classname="FeatureTalkPageStyles-Benefit_Item">
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Image}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Image"
                >
                  <img
                    className={FeatureTalkPageStyles.Benefit_Item_Illustration}
                    src="/assets/images/talk/illustration_benefit_5.png"
                    alt="顧客の背景や状態を理解しながら対話する"
                  />
                </div>
                <div
                  className={FeatureTalkPageStyles.Benefit_Item_Content}
                  data-classname="FeatureTalkPageStyles-Benefit_Item_Content"
                >
                  <dl
                    className={FeatureTalkPageStyles.Benefit_Item_Catalog}
                    data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog"
                  >
                    <dt
                      className={FeatureTalkPageStyles.Benefit_Item_Catalog_Heading}
                      data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Heading"
                    >
                      顧客の
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        背景や状態を
                      </b>
                      <br />
                      <b className={FeatureTalkPageStyles.Bold} data-classname="FeatureTalkPageStyles-Bold">
                        理解
                      </b>
                      しながら対話する
                    </dt>
                    <dd>
                      顧客の状態が直感的にわかれば、より丁寧に適切なサポートを行うことができます。例えばどのページを見ているのか、初めてのお問い合わせなのか、リアルタイムに今なにをしているのか。顧客の過去〜今に至る行動を「知る」ことで、サポートのその一言目からを変革しましょう。
                    </dd>
                    <dd
                      className={FeatureTalkPageStyles.Benefit_Item_Catalog_Navigation}
                      data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Navigation"
                    >
                      <a
                        className={FeatureTalkPageStyles.Benefit_Item_Catalog_Button}
                        href="https://karte.io/product/insight/"
                        target="_blank"
                        data-category="body"
                        data-action="navigation"
                        data-label="KARTE Insightを見る"
                        data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Button"
                        rel="noreferrer"
                      >
                        KARTE Insightを見る
                        <FontAwesomeIcon
                          icon={faAngleRight}
                          width="8"
                          className={FeatureTalkPageStyles.Benefit_Item_Catalog_Icon}
                          data-classname="Footer-Catalog_Icon"
                        />
                      </a>
                      <a
                        className={FeatureTalkPageStyles.Benefit_Item_Catalog_Button}
                        href="https://karte.io/product/live/"
                        target="_blank"
                        data-category="body"
                        data-action="navigation"
                        data-label="Live機能を見る"
                        data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Button"
                        rel="noreferrer"
                      >
                        Live機能を見る
                        <FontAwesomeIcon
                          icon={faAngleRight}
                          width="8"
                          className={FeatureTalkPageStyles.Benefit_Item_Catalog_Icon}
                          data-classname="FeatureTalkPageStyles-Benefit_Item_Catalog_Icon"
                        />
                      </a>
                    </dd>
                  </dl>
                </div>
              </li>
            </ul>
          </div>
        </section>
        <div className={FeatureTalkPageStyles.Case_Container} data-classname="FeatureTalkPageStyles-Case_Container">
          <div data-page-category="product">
            <section
              id="interview"
              className={FeatureTalkPageStyles.Case_Section}
              data-classname="FeatureTalkPageStyles-Case_Section"
            >
              <div className={FeatureTalkPageStyles.Case_Inner} data-classname="FeatureTalkPageStyles-Case_Inner">
                <h3
                  className={FeatureTalkPageStyles.Case_Headline}
                  data-classname="FeatureTalkPageStyles-Case_Headline"
                >
                  お客様事例
                </h3>
                <ul className={FeatureTalkPageStyles.Case_Catalog} data-classname="FeatureTalkPageStyles-Case_Catalog">
                  {interviews.map((interview: any, index) => (
                    <li
                      key={`interview_${index}`}
                      className={FeatureTalkPageStyles.Case_Catalog_Item}
                      data-classname="FeatureTalkPageStyles-Case_Catalog_Item"
                    >
                      <a
                        className={FeatureTalkPageStyles.Case_Catalog_Link}
                        href={interview.clipurl}
                        rel="noreferrer noopener"
                        target="_blank"
                        data-category="body"
                        data-action="clip"
                        data-label={interview.title}
                        data-classname="FeatureTalkPageStyles-Case_Catalog_Link"
                      >
                        <div
                          className={FeatureTalkPageStyles.Case_Catalog_Cover}
                          data-classname="FeatureTalkPageStyles-Case_Catalog_Cover"
                        >
                          <img src={interview.imageUrl} alt="" className={FeatureTalkPageStyles.Case_Catalog_Image} />
                        </div>
                        <div
                          className={FeatureTalkPageStyles.Case_Catalog_Inner}
                          data-classname="FeatureTalkPageStyles-Case_Catalog_Inner"
                        >
                          <div
                            className={FeatureTalkPageStyles.Case_Catalog_Headline}
                            data-classname="FeatureTalkPageStyles-Case_Catalog_Headline"
                          >
                            {interview.title}
                          </div>
                          <div
                            className={FeatureTalkPageStyles.Case_Catalog_Summary}
                            data-classname="FeatureTalkPageStyles-Case_Catalog_Summary"
                          >
                            <span>{interview.company}</span>
                            <span> / </span>
                            <span>{interview.product}</span>
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
                <a
                  className={FeatureTalkPageStyles.Case_Button}
                  href="https://cxclip.karte.io/tag/popular-word/karte-talk/"
                  rel="noreferrer noopener"
                  target="_blank"
                  data-category="body"
                  data-action="clip"
                  data-label="すべてのお客様事例を見る"
                  data-classname="FeatureTalkPageStyles-Case_Button"
                >
                  すべてのお客様事例を見る
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    width="8"
                    className={FeatureTalkPageStyles.Case_Button_Icon}
                    data-classname="FeatureTalkPageStyles-Case_Button_Icon"
                  />
                </a>
              </div>
            </section>
            <div
              className={FeatureTalkPageStyles.Logos_Container}
              data-classname="FeatureTalkPageStyles-Logos_Container"
            >
              <h3
                className={FeatureTalkPageStyles.Logos_Headline}
                data-classname="FeatureTalkPageStyles-Logos_Headline"
              >
                導入実績
              </h3>
              <ul className={FeatureTalkPageStyles.Logos_Catalog} data-classname="FeatureTalkPageStyles-Logos_Catalog">
                {logos.map((logo: any, index) => (
                  <li key={`logo_${index}`}>
                    <img
                      src={logo.imageUrl}
                      alt={logo.companyName}
                      className={FeatureTalkPageStyles.Logos_Catalog_Image}
                      data-classname="FeatureTalkPageStyles-Logos_Catalog_Image"
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <section
            className={FeatureTalkPageStyles.Product_Container}
            data-classname="FeatureTalkPageStyles-Product_Container"
            id="feature"
          >
            <h3
              className={FeatureTalkPageStyles.Product_Headline}
              data-classname="FeatureTalkPageStyles-Product_Headline"
            >
              KARTE Talk
              <br className={SiteWideStyles.DisplayMobileOnly} />
              の主な機能
            </h3>
            <motion.div
              {...(open ? { className: classNames(FeatureTalkPageStyles.Product_Outer, open && 'open') } : {})}
              animate={{ height: heightAnimation }}
              initial={{ height: open ? '380px' : 'auto' }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              data-classname="FeatureTalkPageStyles-Product_Outer"
            >
              <ul
                ref={featureInnerRef}
                className={FeatureTalkPageStyles.Product_Catalog}
                data-classname="FeatureTalkPageStyles-Product_Catalog"
              >
                {features.map((feature, index) => (
                  <li
                    key={`feature_${index}`}
                    className={FeatureTalkPageStyles.Product_Catalog_Item}
                    data-classname="FeatureTalkPageStyles-Product_Catalog_Item"
                  >
                    <img
                      className={FeatureTalkPageStyles.Product_Catalog_Icon}
                      src={`/assets/images/talk/${feature.icon}.png`}
                      alt={feature.title}
                      data-classname="FeatureTalkPageStyles-Product_Catalog_Icon"
                    />
                    <dl
                      className={FeatureTalkPageStyles.Product_Catalog_Description}
                      data-classname="FeatureTalkPageStyles-Product_Catalog_Description"
                    >
                      <dt
                        className={FeatureTalkPageStyles.Product_Catalog_Headline}
                        data-classname="FeatureTalkPageStyles-Product_Catalog_Headline"
                      >
                        {feature.title}
                      </dt>
                      <dd
                        className={FeatureTalkPageStyles.Product_Catalog_Summary}
                        data-classname="FeatureTalkPageStyles-Product_Catalog_Summary"
                      >
                        {feature.text}
                      </dd>
                    </dl>
                  </li>
                ))}
              </ul>
            </motion.div>
            <div
              className={classNames(
                SiteWideStyles.DisplayMobileOnly,
                FeatureTalkPageStyles.Product_Button_Outer,
                open && 'open'
              )}
              data-classname="FeatureTalkPageStyles-Product_Product_Button_Outer"
            >
              <button
                type="button"
                onClick={toggleOpen}
                className={FeatureTalkPageStyles.Product_Button_More}
                data-classname="FeatureTalkPageStyles-Product_Button_More"
              >
                {open ? '閉じる' : '機能をもっとみる'}
                <FontAwesomeIcon
                  icon={faAngleDown}
                  width="12"
                  className={FeatureTalkPageStyles.Product_Button_Icon}
                  data-classname="Footer-Catalog_Icon"
                />
              </button>
            </div>
          </section>
          <ContactBanner />
        </div>
      </main>
    </Layout>
  )
}

export default FeatureTalkPage

export const Head: HeadFC = () => {
  return (
    <Seo
      title="KARTE Talk｜ウェブチャットでの双方向コミュニケーション"
      description="CX（顧客体験）プラットフォーム KARTE（カルテ）では、サポートコストを低減しながら、ウェブチャット/FAQテンプレートを用いてスムーズな顧客サポートを実現します。"
      pathname="/product/talk/"
      contentType="website"
      language="ja"
      socialImage={{
        isLocal: true,
        url: OGImage,
      }}
    />
  )
}
